/**
 * Decléor — scroll-pinned ingredient carousel.
 *
 * While the section is pinned, scrolling the page DOWN moves the ingredient panels sideways, the way
 * the Lenis site moves its showcase. The page never stops scrolling — the section is `position: sticky`
 * inside a tall runway, so the scrollbar, the keyboard, a trackpad flick and Lenis's own easing all
 * keep working exactly as they do everywhere else. Nothing here captures or cancels a gesture.
 *
 * IT DRIVES scrollLeft, NOT A TRANSFORM. The track is already a real horizontal scroll container with
 * `flex: 0 0 100%` slides — the same container the phone swipes natively. Writing its scrollLeft means
 * the existing assets/focus-carousel.js keeps working untouched: that file reconciles the active dot
 * from the track's scroll position, so the ingredient thumbnails light up as the panels pass with no
 * coordination between the two scripts. A transformed strip would have needed all of that rebuilt.
 *
 * The CSS turns the track's own `overflow-x` to hidden while pinned, so the wheel cannot drive it
 * directly at the same time as the page — two inputs on one scrollLeft is the classic way this effect
 * ends up feeling broken. `overflow: hidden` still allows the programmatic write below.
 *
 * IT TRAILS THE PAGE ON PURPOSE. The raw target is a pure function of the scroll position, which moves
 * the panels in perfect lockstep with the wheel — correct, and lifeless. Easing the track toward that
 * target instead gives the panels a little weight: they set off a beat after the page and settle a beat
 * after it stops. The merchant sets how much ("Horizontal lag"); at 0 the easing is bypassed entirely
 * and the old exact behaviour is back, to the pixel.
 *
 * The loop only runs while there is distance left to close, and stops itself within half a pixel — so a
 * page sitting still costs nothing, and this never becomes a permanent rAF.
 *
 * WHERE THE PROGRESS COMES FROM: the runway's own box. Its top edge relative to the viewport goes from
 * 0 (the moment the section pins) to -(runway height - pinned height) (the moment it unpins), and that
 * ratio is the position in the track. Read in a rAF off the scroll event and written only when it
 * actually changed, so a scroll that does not move the section costs one subtraction.
 *
 * WHO DECIDES WHETHER IT RUNS: both files, each owning what it can actually know. The stylesheet owns
 * width and prefers-reduced-motion — a media query is exactly right for those. This script owns
 * whether the content FITS, which no media query can know, because the limiting element is the
 * ingredient text and its height changes with the product and with the translation. See evaluate().
 */
(function () {
  'use strict';

  var pins = document.querySelectorAll('[data-fa-pin]');
  if (!pins.length) {
    return;
  }

  var frame = 0;
  var items = [];
  var i;

  /* WHETHER TO PIN AT ALL — measured per product, not decided by a breakpoint.
   *
   * The pin only works if the tallest slide fits the space the pinned box has, and that space is the
   * viewport less the sticky header. Whether it fits is a property of the CONTENT: the limiting element
   * is the ingredient TEXT column, so the same window is comfortable for a short description and too
   * small for a long one — and a German translation of the same field runs longer than the French.
   * A `min-height` media query cannot know any of that. It was tried: a floor of 860px, measured on one
   * product, which then excluded every laptop whose window is shorter and would still have been wrong
   * for a product with three lines of text.
   *
   * So: apply the class, read the result, and take it back if it does not fit. Reading after applying
   * is what makes the answer honest — the pinned state caps the image, so measuring the unpinned
   * section would judge a layout that never renders. One forced reflow per resize, which is where a
   * reflow is happening anyway.
   *
   * The CSS keeps ownership of width and motion preference: if its media query does not match, the
   * runway never becomes `position: relative` and the check below removes the class again, so the two
   * can never disagree. */
  /* Ask the slide itself whether it overflows, rather than adding up the parts.
   *
   * The first version of this summed the nav, the tallest slide's scrollHeight and some headroom, and
   * compared that to the section — and it was wrong in a way that only showed up at one window size:
   * scrollHeight does not include a child's top MARGIN, and the grid inside each slide has one. At an
   * 800px window the sum said "fits" by a few pixels while the slide was in fact clipped.
   *
   * Reading each slide's own overflow after the class is applied cannot make that mistake: whatever
   * the parts are, whatever margins collapse or do not, `scrollHeight > clientHeight` is the browser
   * telling us the content did not fit the box it actually got. */
  function fits(it) {
    var slides = it.section.querySelectorAll('.dc-fa__slide');
    var n;
    for (n = 0; n < slides.length; n += 1) {
      if (slides[n].scrollHeight > slides[n].clientHeight) {
        return false;
      }
    }
    return true;
  }

  /* THE PINNED BOX IS NOT CENTRED ON THE SCREEN, and this is what pays the difference back.
   *
   * It runs from the header's height down to the bottom of the viewport, so `justify-content: center`
   * centres the content inside a box whose top band is reserved for a header that, while you scroll
   * down, is not drawn at all. The visible gap above the thumbnails was therefore one header taller
   * than the gap under the image — measured at 1000 / 1084 / 1200px of viewport as 136/29, 170/63 and
   * 224/117, a difference of exactly 107px each time.
   *
   * Reserving the same height at the BOTTOM re-centres it on the true middle of the screen. Writing
   * that as a flat padding in the stylesheet is the obvious fix and it is the wrong one: it spends
   * 107px this section does not have. At a 1000px viewport there are only 59px of free space, so the
   * content would overflow, fits() would decline, and the pin would be lost on every laptop between
   * 950 and 1085px.
   *
   * So the reserve is the SMALLER of the header height and the free space that actually exists: full
   * correction on a tall screen, partial on a middling one, none on a short one — and never at the
   * cost of the pin. The free space is read with the reserve zeroed, because otherwise it would be
   * measuring its own last answer. The media cap is keyed to 100vh rather than to this box, so the
   * padding cannot change the content height between that read and the write.
   *
   * fits() is asked again afterwards and the whole thing is given back if it now says no. That is belt
   * and braces against sub-pixel rounding rather than a case anyone has seen, and it costs one read. */
  function balance(it) {
    var section = it.section;
    var kids = section.children;
    var top;
    var first = null;
    var last = null;
    var rect;
    var n;
    var pad;

    section.style.setProperty('--dc-fa-balance', '0px');

    top = parseFloat(window.getComputedStyle(section).top);
    if (Number.isNaN(top) || top <= 0) {
      return;
    }

    for (n = 0; n < kids.length; n += 1) {
      rect = kids[n].getBoundingClientRect();
      if (rect.height > 0) {
        if (first === null || rect.top < first) {
          first = rect.top;
        }
        if (last === null || rect.bottom > last) {
          last = rect.bottom;
        }
      }
    }
    if (first === null) {
      return;
    }

    pad = section.getBoundingClientRect().height - (last - first);
    if (pad > top) {
      pad = top;
    }
    if (pad < 0) {
      pad = 0;
    }

    section.style.setProperty('--dc-fa-balance', Math.round(pad) + 'px');
    if (!fits(it)) {
      section.style.setProperty('--dc-fa-balance', '0px');
    }
  }

  function evaluate() {
    var n;
    var it;
    for (n = 0; n < items.length; n += 1) {
      it = items[n];
      it.runway.classList.add('is-pinning');
      /* Read AFTER adding the class: getComputedStyle answers whether the CSS accepted the pin at this
         width and motion preference, and the measurement below is then of the layout that will
         actually render. */
      if (window.getComputedStyle(it.runway).position !== 'relative' || !fits(it)) {
        it.runway.classList.remove('is-pinning');
        it.section.style.setProperty('--dc-fa-balance', '0px');
      } else {
        balance(it);
      }
    }
  }

  function pinned(runway) {
    return runway.classList.contains('is-pinning');
  }

  /* Write only on change. A custom property write is cheap but it still invalidates style for the
     subtree, and at 60fps across four properties and three slides that adds up for nothing. */
  function setVar(el, name, value) {
    var next = String(Math.round(value * 1000) / 1000);
    if (el.__dcVars[name] !== next) {
      el.__dcVars[name] = next;
      el.style.setProperty(name, next);
    }
  }

  /* Each slide's position relative to the viewport of the track, in panels:
   *   off   signed, 0 when the slide is centred, ±1 when it is one panel away
   *   focus 1 when centred, 0 by one panel away — the value nearly every effect keys on
   * Derived from scrollLeft rather than from getBoundingClientRect on each slide: one read of a number
   * the loop already has, instead of n forced layouts per frame. */
  function writeSlideVars(it, panelWidth) {
    var n;
    var off;
    if (!panelWidth) {
      return;
    }
    for (n = 0; n < it.slides.length; n += 1) {
      off = (n * panelWidth - it.current) / panelWidth;
      setVar(it.slides[n], '--dc-fa-off', off);
      setVar(it.slides[n], '--dc-fa-focus', Math.max(0, 1 - Math.abs(off)));
    }
  }

  function update() {
    frame = 0;
    var n;
    var it;
    var runway;
    var track;
    var rect;
    var secRect;
    var travel;
    var span;
    var progress;
    var target;
    var rounded;
    var velocity;
    var offset;
    var r;
    var moving = false;

    for (n = 0; n < items.length; n += 1) {
      it = items[n];
      runway = it.runway;
      track = it.track;
      if (!pinned(runway)) {
        continue;
      }
      rect = runway.getBoundingClientRect();
      /* Velocity from the runway's own movement between frames, normalised against a brisk flick
         (120px in one frame) and clamped. Lenis exposes a velocity of its own, but reading it here
         would tie this file to a library the section does not otherwise need, and would be wrong under
         reduced motion where Lenis never boots. This works either way. */
      velocity = it.lastTop === null ? 0 : Math.min(1, Math.abs(rect.top - it.lastTop) / 120);
      /* A DEAD BAND, and it is not cosmetic. rect.top is a float and Lenis keeps micro-adjusting the
         real scroll position long after a gesture ends, so `velocity > 0` was true essentially forever
         and the loop never stopped — measured, 280 rAF/s at rest against a 240 baseline. Anything under
         a twentieth of a pixel per frame is not motion, it is the smoothing settling. */
      if (velocity < 0.0005) {
        velocity = 0;
      }
      setVar(runway, '--dc-fa-vel', velocity);
      it.lastTop = rect.top;
      /* Only while it is actually moving. Keeping the loop alive unconditionally here would have made
         this a permanent 60fps rAF — the exact thing the settle-and-stop design exists to avoid. When
         the page stops, one more frame runs, writes 0, and the loop ends. */
      if (velocity > 0) {
        moving = true;
      }
      /* How far the track can travel, and how far the page must scroll to spend it. */
      secRect = it.section.getBoundingClientRect();
      travel = track.scrollWidth - track.clientWidth;
      span = rect.height - secRect.height;
      if (travel <= 0 || span <= 0) {
        continue;
      }
      /* PROGRESS IS HOW FAR THE SECTION HAS SLID INSIDE ITS RUNWAY — not where the runway is
         relative to the viewport's top edge.

         This was `-rect.top / span`, which measures from y=0. The section does not pin at y=0: it
         pins at `top: var(--menu-height)`, clear of the sticky header. So the whole ramp sat one
         header-height late and ran out one header-height early, and the LAST ingredient never
         reached the middle of the screen — the carousel simply stopped mid-transition when the
         section unpinned. Measured at 1728x1084 with a 107px header: --dc-fa-progress read 0.89 at
         the exact last frame of the pin (1 - 107/974), the track sat at 1538 of a possible 1728, and
         the last panel was left 190px to the right of centre. The shortfall is proportional to the
         header, so a market whose header is taller shows it worse — which is how it was spotted.

         The difference between the two tops IS that slide, and it needs to know nothing about the
         header: 0 before the section sticks (it is the runway's only child, so the two tops are the
         same box edge), then growing to exactly `span` at the frame the section's bottom meets the
         runway's bottom and it lets go. Both ends are now exact rather than approximately right. */
      progress = (secRect.top - rect.top) / span;
      if (progress < 0) {
        progress = 0;
      }
      if (progress > 1) {
        progress = 1;
      }
      target = progress * travel;

      /* ---- the values every effect reads, written once per frame ----------------------------------
       * Custom properties rather than direct style writes: the stylesheet decides what each one means
       * and which effects exist at all, so a merchant turning one off in the editor removes the rule
       * and this loop keeps writing a number nobody reads — which costs nothing. */
      setVar(runway, '--dc-fa-progress', progress);
      writeSlideVars(it, track.clientWidth);
      /* The countdown ring, repurposed. pathLength="100" means the dash units are already percentages,
         so the offset IS 100 minus the percentage done — no circumference maths.
         Written to EVERY ring, not just the first: focus-carousel.js moves `.is-active` between the
         thumbnails as the panels pass, and only the active one is visible, so driving one ring would
         leave the gauge frozen the moment the second ingredient took over. */
      offset = String(Math.round((1 - progress) * 100));
      for (r = 0; r < it.rings.length; r += 1) {
        if (it.rings[r].__dcOffset !== offset) {
          it.rings[r].__dcOffset = offset;
          it.rings[r].style.strokeDashoffset = offset;
        }
      }

      if (it.lerp >= 1) {
        /* Lag 0: no easing at all, and deliberately not "a lerp of 1" dressed up — the merchant asked
           for exact, so the target is written straight through. */
        it.current = target;
      } else {
        /* A proportional follow, the same shape Lenis uses for the page. Snapping the last fraction of
           a pixel is what lets the loop stop; without it the difference halves forever and the rAF
           never ends. */
        it.current += (target - it.current) * it.lerp;
        if (Math.abs(target - it.current) < 0.5) {
          it.current = target;
        } else {
          moving = true;
        }
      }

      /* Only write on a real change: an unchanged scrollLeft still fires a scroll event on the track,
         which would wake focus-carousel.js's own reconcile for nothing. */
      rounded = Math.round(it.current);
      if (Math.abs(track.scrollLeft - rounded) > 0.5) {
        track.scrollLeft = rounded;
      }
    }

    /* Keep the loop alive only while something is still closing a distance. */
    if (moving) {
      frame = window.requestAnimationFrame(update);
    }
  }

  function schedule() {
    if (frame) {
      return;
    }
    frame = window.requestAnimationFrame(update);
  }

  function remeasure() {
    evaluate();
    schedule();
  }

  for (i = 0; i < pins.length; i += 1) {
    var section = pins[i].querySelector('.dc-fa');
    var track = pins[i].querySelector('[data-fa-track]');
    if (section && track) {
      /* 0 % lag → lerp 1 (exact). 90 % → lerp 0.1 (heavy). The attribute is the merchant's setting;
         an absent or unparseable value falls back to the schema default rather than to no easing. */
      var smooth = parseFloat(pins[i].getAttribute('data-fa-smooth'));
      if (isNaN(smooth)) {
        smooth = 70;
      }
      pins[i].__dcVars = {};
      var slides = track.querySelectorAll('[data-fa-slide]');
      var k;
      for (k = 0; k < slides.length; k += 1) {
        slides[k].__dcVars = {};
      }
      items.push({
        runway: pins[i],
        section: section,
        track: track,
        slides: slides,
        /* Every thumbnail's arc: whichever one is currently visible shows the section's progress. */
        rings: section.querySelectorAll('[data-fa-ring]'),
        lerp: Math.max(0.05, 1 - smooth / 100),
        current: track.scrollLeft,
        lastTop: null,
      });
    }
  }
  if (!items.length) {
    return;
  }

  /* The theme's own bus rather than a raw scroll listener: `theme:scroll` is what Lenis and theme.js
     both drive, so this stays in step with the smoothed position instead of racing it. The native
     events are kept as a fallback for the reduced-motion case, where Lenis does not boot at all and
     `theme:scroll` may never fire. */
  /* THE THUMBNAILS HAVE TO MOVE THE PAGE, NOT THE TRACK.
   *
   * While pinned, the page's scroll position IS the track's position — update() rewrites scrollLeft
   * from it on the very next frame. So focus-carousel.js setting scrollLeft on a click was overwritten
   * a frame later, and the panel was left stranded wherever the two had met: measured, the slide sat at
   * a --dc-fa-focus of about 0.5, which is why the title and the body copy stayed half faded. The click
   * did something; it just could not survive.
   *
   * Clicking a thumbnail now scrolls the PAGE to the point in the runway where that panel is centred,
   * and everything else follows from that on its own — the track, the dots, the halo, the ring. One
   * source of truth instead of two fighting.
   *
   * CAPTURE PHASE, and stopped there: focus-carousel.js binds in the bubble phase, so intercepting on
   * the way down is what keeps it from also writing scrollLeft. It is only stopped while the pin is
   * actually on; unpinned, the click passes straight through and the carousel behaves exactly as it
   * always has. Keyboard is covered by the same path — Enter and Space on a <button> dispatch a click.
   *
   * scrollTo with `behavior: smooth`, which assets/smooth-scroll.js routes through Lenis (see
   * CLAUDE.md), so the journey is eased by the same engine that eases everything else rather than by a
   * second animation running alongside it. */
  document.addEventListener(
    'click',
    function (event) {
      var dot = event.target.closest ? event.target.closest('[data-fa-dot]') : null;
      if (!dot) {
        return;
      }
      var runway = dot.closest('[data-fa-pin]');
      if (!(runway && pinned(runway))) {
        return;
      }
      var it = null;
      var n;
      for (n = 0; n < items.length; n += 1) {
        if (items[n].runway === runway) {
          it = items[n];
        }
      }
      if (!it) {
        return;
      }
      var dots = runway.querySelectorAll('[data-fa-dot]');
      var index = Array.prototype.indexOf.call(dots, dot);
      if (index < 0 || dots.length < 2) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      var rect = runway.getBoundingClientRect();
      var span = rect.height - it.section.getBoundingClientRect().height;
      var top = rect.top + window.pageYOffset;
      /* Same header offset as the loop above, and it has to be undone here too: progress is now
         measured from where the section PINS, so the scroll position that produces progress f is
         `runwayTop - stickyTop + f * span`. Without the subtraction, clicking the last thumbnail
         landed at 1 - stickyTop/span and stopped short of its own ingredient.

         Read off the section's resolved `top` rather than from its rect, because at click time the
         section may not be stuck yet and its rect would then answer where it happens to sit. A
         non-px value (`auto`, before the sticky rules apply on a phone) parses to NaN, so it falls
         back to 0 — which is the correct offset for a section that is not pinned at all. */
      var stickyTop = parseFloat(window.getComputedStyle(it.section).top);
      if (Number.isNaN(stickyTop)) {
        stickyTop = 0;
      }
      window.scrollTo({
        top: Math.round(top - stickyTop + (index / (dots.length - 1)) * span),
        behavior: 'smooth',
      });
    },
    true
  );

  document.addEventListener('theme:scroll', schedule);
  window.addEventListener('scroll', schedule, { passive: true });
  /* Resize re-decides as well as re-positions: a window that just became tall enough should start
     pinning, and one that shrank below the content should hand the section back. */
  window.addEventListener('resize', remeasure, { passive: true });
  /* Fonts change the text column's height, which is the element the decision turns on. */
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(remeasure);
  }
  remeasure();
})();
